import {
  ROUTE_MERGE_COMPANIES,
  ROUTE_PARAM_MERGE_COMPANIES_USER_ID_KEEP,
  ROUTE_PARAM_MERGE_COMPANIES_USER_ID_REMOVE,
} from "@/constants/routes.constants";
import { fetchUsersWithCompanies } from "@/models/socket/company.model";
import {
  MergeCompaniesContext,
  defaultMergeCompaniesContext,
} from "@/services/MergeCompaniesContext/mergeCompaniesContext";
import { DBBranchesByCompanyID } from "@/types";
import { genericFailureToast } from "@/utils/utils.util";
import React, { ReactNode, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router";

type MergeCompaniesProviderProps = {
  children: ReactNode;
};

const MergeCompaniesProvider: React.FC<MergeCompaniesProviderProps> = ({
  children,
}) => {
  const [allCompanies, setAllCompanies] = useState<DBBranchesByCompanyID[]>(
    defaultMergeCompaniesContext.allCompanies
  );
  const [allUsers, setAllUsers] = useState(
    defaultMergeCompaniesContext.allUsers
  );
  const [keepSideUser, setKeepSideUser] = useState(
    defaultMergeCompaniesContext.keepSideUser
  );
  const [removeSideUser, setRemoveSideUser] = useState(
    defaultMergeCompaniesContext.removeSideUser
  );
  const [keepSideCompany, setKeepSideCompany] =
    useState<DBBranchesByCompanyID | null>(
      defaultMergeCompaniesContext.keepSideCompany
    );
  const [removeSideCompany, setRemoveSideCompany] =
    useState<DBBranchesByCompanyID | null>(
      defaultMergeCompaniesContext.removeSideCompany
    );
  const [mergedCompany, setMergedCompany] =
    useState<DBBranchesByCompanyID | null>(
      defaultMergeCompaniesContext.mergedCompany
    );

  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [successfulSubmission, setSuccessfulSubmission] = useState<boolean>(
    defaultMergeCompaniesContext.successfulSubmission
  );

  const location = useLocation();
  const nav = useNavigate();

  // Memoize urlParams to prevent unnecessary re-renders
  const urlParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );

  async function fetchSelectableUsersAndCompanies() {
    setLoading(true);
    setError(null);
    const response = await fetchUsersWithCompanies();
    if (response.rStatus === "success") {
      setAllUsers(response.rData.users);
      setAllCompanies(response.rData.companies);
    } else {
      console.error("Error fetching users:", response.rData);
      setError(response.rData);
    }
    setLoading(false);
  }

  useEffect(() => {
    fetchSelectableUsersAndCompanies();
  }, []);

  useEffect(() => {
    if (!allUsers.length) {
      return;
    }
    if (!allCompanies.length) {
      return;
    }

    const userIDKeep = urlParams.get(ROUTE_PARAM_MERGE_COMPANIES_USER_ID_KEEP);
    console.log(`User ID from URL: ${userIDKeep}`);

    if (!userIDKeep) {
      setKeepSideUser(null);
      setKeepSideCompany(null);
    } else {
      // find the user in the selectableUsers array
      const userKeep = allUsers.find((user) => user.id === userIDKeep);
      if (userKeep) {
        setKeepSideUser(userKeep);
        if (userKeep.company_ids != null && userKeep.company_ids.length == 1) {
          // If the user has only one company, set it as the keep side company
          const userKeepCompanyIds = userKeep.company_ids;
          const company = allCompanies.find(
            (company) => company.company_id === userKeepCompanyIds[0]
          );

          if (company) {
            setKeepSideCompany(company);
          } else {
            console.warn(
              `Company with ID ${userKeep.company_ids[0]} not found in selectable companies.`
            );
            setKeepSideCompany(null);
            genericFailureToast(
              `Company with ID ${userKeep.company_ids[0]} not found. Please select a valid company.`
            );
          }
        }
      } else {
        console.warn(
          `User with ID ${userIDKeep} not found in selectable users.`
        );
        setKeepSideUser(null);
        genericFailureToast(
          `User with ID ${userIDKeep} not found. Please select a valid user.`
        );
      }
    }

    const userIDRemove = urlParams.get(
      ROUTE_PARAM_MERGE_COMPANIES_USER_ID_REMOVE
    );
    if (!userIDRemove) {
      setRemoveSideUser(null);
      setRemoveSideCompany(null);
    } else {
      // if (userIDRemove === userIDKeep) {
      //   console.warn(
      //     `User ID for removal (${userIDRemove}) is the same as keep user ID (${userIDKeep}).`
      //   );
      //   setRemoveSideUser(null);
      //   setRemoveSideCompany(null);
      //   genericFailureToast(
      //     `User for removal cannot be the same as keep user. Please select a different user.`
      //   );
      //   return;
      // }

      // find the user in the selectableUsers array
      const userRemove = allUsers.find((user) => user.id === userIDRemove);
      if (userRemove) {
        setRemoveSideUser(userRemove);
        if (
          userRemove.company_ids != null &&
          userRemove.company_ids.length === 1
        ) {
          // If the user has only one company, set it as the remove side company
          const userRemoveCompanyIds = userRemove.company_ids;
          const company = allCompanies.find(
            (company) => company.company_id === userRemoveCompanyIds[0]
          );

          if (company) {
            setRemoveSideCompany(company);
          } else {
            console.warn(
              `Company with ID ${userRemove.company_ids[0]} not found in selectable companies.`
            );
            setRemoveSideCompany(null);
            genericFailureToast(
              `Company with ID ${userRemove.company_ids[0]} not found. Please select a valid company.`
            );
          }
        }
      } else {
        console.warn(
          `User with ID ${userIDRemove} not found in selectable users.`
        );
        setRemoveSideUser(null);
        genericFailureToast(
          `User with ID ${userIDRemove} not found. Please select a valid user.`
        );
      }
    }
  }, [allUsers, urlParams, allCompanies]);

  useEffect(() => {
    if (!keepSideCompany) {
      setMergedCompany(null);
      return;
    }

    if (!removeSideCompany) {
      setMergedCompany(keepSideCompany);
    }

    // add all the branches from the remove side company to the keep side company, save to mergedCompany
    const mergedBranches = [
      ...keepSideCompany.branches,
      ...(removeSideCompany?.branches || []),
    ];
    const mergedCompanyFull: DBBranchesByCompanyID = {
      ...keepSideCompany,
      branches: mergedBranches,
    };
    console.log(
      `Merged company created with ${mergedBranches.length} branches from keep and remove sides.`
    );
    setMergedCompany(mergedCompanyFull);
  }, [keepSideCompany, removeSideCompany]);

  function resetStates() {
    setLoading(true);
    setKeepSideUser(null);
    setRemoveSideUser(null);
    setKeepSideCompany(null);
    setRemoveSideCompany(null);
    setMergedCompany(null);
    setSuccessfulSubmission(false);
    nav(`${ROUTE_MERGE_COMPANIES}`);
    fetchSelectableUsersAndCompanies();
  }

  useEffect(() => {
    // Ensure companies do have the same ID
    // Cannot select the same company for both sides of the merge.
    if (!keepSideCompany || !removeSideCompany) {
      return;
    }
    if (keepSideCompany.company_id === removeSideCompany.company_id) {
      console.warn(
        `Keep side company (${keepSideCompany.company_id}) is the same as remove side company (${removeSideCompany.company_id}).`
      );
      setRemoveSideCompany(null);
      genericFailureToast(
        `Cannot select the same company for both sides of the merge.`
      );
      return;
    }
  }, [keepSideCompany, removeSideCompany]);

  return loading ? (
    <div className="py-4">
      <div className="flex items-center justify-center">
        <div className="h-10 w-10 animate-spin rounded-full border-b-3 border-white"></div>
      </div>
      <div className="flex items-center justify-center">
        <h2 className="mt-2 text-lg text-white">Loading Users Details...</h2>
      </div>
    </div>
  ) : error ? (
    <div className="text-red-500">Error: {error}</div>
  ) : allUsers.length === 0 ? (
    <div className="flex items-center justify-center">
      <p className="text-red-500">No users available for selecting.</p>
    </div>
  ) : allCompanies.length === 0 ? (
    <div className="flex items-center justify-center">
      <p className="text-red-500">No companies available for selecting.</p>
    </div>
  ) : (
    <MergeCompaniesContext.Provider
      value={{
        allCompanies: allCompanies,
        allUsers: allUsers,
        keepSideUser: keepSideUser,
        removeSideUser: removeSideUser,
        keepSideCompany: keepSideCompany,
        removeSideCompany: removeSideCompany,
        setKeepSideCompany: setKeepSideCompany,
        setRemoveSideCompany: setRemoveSideCompany,
        mergedCompany: mergedCompany,
        successfulSubmission: successfulSubmission,
        setSuccessfulSubmission: setSuccessfulSubmission,
        resetStates: resetStates,
      }}
    >
      {children}
    </MergeCompaniesContext.Provider>
  );
};

export default MergeCompaniesProvider;
